
import { useMemo } from "react";
import { Strategy } from "@/components/marketplace/types/marketplace-types";
import { AccountPerformanceData } from "@/components/marketplace/types/strategy-card-types";
import MarketplaceFilters from "@/components/marketplace/MarketplaceFilters";
import StrategiesList from "@/components/marketplace/StrategiesList";
import { useCountryRestrictions } from "@/hooks/useCountryRestrictions";
import {
  getRiskLabel,
  formatDate,
  getExchangeLogo,
} from "@/components/marketplace/utils/marketplace-utils";
import { MarketplaceStrategy } from "@/types/strategy-types";
import { useIsMobile } from "@/hooks/use-mobile";

interface MarketplaceContentProps {
  strategies: any[];
  loading: boolean;
  performanceLoading: boolean;
  accountPerformance: Record<string, AccountPerformanceData>;
  showMoreFilters: boolean;
  setShowMoreFilters: (show: boolean) => void;
  topPerformers: boolean;
  setTopPerformers: (top: boolean) => void;
  selectedStrategy: string | null;
  setSelectedStrategy: (strategy: string | null) => void;
  filters: any;
  setFilters: (filters: any) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
}

const MarketplaceContent = ({
  strategies,
  loading,
  performanceLoading,
  accountPerformance,
  showMoreFilters,
  setShowMoreFilters,
  topPerformers,
  setTopPerformers,
  selectedStrategy,
  setSelectedStrategy,
  filters,
  setFilters,
  sortBy,
  setSortBy
}: MarketplaceContentProps) => {
  const isMobile = useIsMobile();
  const { isExchangeBlocked, loading: countryLoading } = useCountryRestrictions();
  
  // Sort strategies by PNL ratio if sortBy is 'performance'
  const sortedStrategies = useMemo(() => {
    if (sortBy === 'performance' && !loading && !performanceLoading) {
      return [...strategies].sort((a, b) => {
        const aPnlRatio = accountPerformance[a.id]?.pnl_ratio ?? 0;
        const bPnlRatio = accountPerformance[b.id]?.pnl_ratio ?? 0;
        return bPnlRatio - aPnlRatio;
      });
    }
    return strategies;
  }, [strategies, accountPerformance, sortBy, loading, performanceLoading]);

  // Add console.log to debug strategy data
  console.log('Sorted strategies with premium and country restriction status:', sortedStrategies.map(s => ({
    name: s.name || s.strategy_name,
    plan: s.plan, 
    isPremium: s.plan === 'premium' || s.plan === 'pro',
    isBlocked: isExchangeBlocked(s.exchange)
  })));

  return (
    <div className="relative">
      <MarketplaceFilters
        filters={filters}
        setFilters={setFilters}
        topPerformers={topPerformers}
        setTopPerformers={setTopPerformers}
        showMoreFilters={showMoreFilters}
        setShowMoreFilters={setShowMoreFilters}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />

      <div className={`pt-6 pb-16 ${isMobile ? 'mt-16' : ''}`}>
        <StrategiesList
          strategies={sortedStrategies as unknown as MarketplaceStrategy[]}
          isLoading={loading || performanceLoading || countryLoading}
          onSelectStrategy={(strategyName: string) => {
            setSelectedStrategy(strategyName);
          }}
          getRiskLabel={getRiskLabel}
          formatDate={formatDate}
          getExchangeLogo={getExchangeLogo}
          accountPerformanceData={accountPerformance}
          isExchangeBlocked={isExchangeBlocked}
        />
      </div>
    </div>
  );
};

export default MarketplaceContent;
